import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronLeft, ChevronRight, Globe, Feather, Users, Award, ArrowRight } from 'lucide-react';
import { useEmailVerification, EmailVerificationModal } from '../components/EmailVerification';

// Static data - no database needed
const brands = [
  { id: 1, name: "Waphyto", category: "Total Care", image: "/images/brands/seichajo.jpg", isLogo: true },
  { id: 2, name: "Seichajo Yamashina", category: "Matcha", image: "/images/brands/waphyto.png", isLogo: true },
  { id: 3, name: "Tamanohada", category: "Body", image: "/images/brands/tamanohada.png", isLogo: true },
  { id: 4, name: "Sophistance", category: "Skincare", image: "/images/brands/sophistance.png", isLogo: true },
  { id: 5, name: "BelAge", category: "Supplement", image: "/images/brands/belage.png", isLogo: true },
  { id: 6, name: "Grand Fond Blanc", category: "Lifestyle", image: "/images/brands/grandfond.jpg", isLogo: true },
  { id: 7, name: "Fairydrops", category: "Makeup", image: "/images/brands/fairydrops.png", isLogo: true },
  { id: 8, name: "Lacasta", category: "Hair Care", image: "/images/brands/lacasta.jpg", isLogo: true },
  { id: 9, name: "Mesoins", category: "Self Care", image: "/images/brands/mesoins.png", isLogo: true },
  { id: 10, name: "Arpa", category: "Fragrance", image: "/images/brands/arpa.png", isLogo: true },
  { id: 11, name: "Intime Organics", category: "Intimate Care", image: "/images/brands/intime.png", isLogo: true },
  { id: 12, name: "Loqzin", category: "Skincare", image: "/images/brands/loqzin.jpg", isLogo: true },
];

const services = [
  { id: 1, title: "Market Expansion", icon: "Globe", description: "We provide tailored market expansion strategies for Japanese beauty and lifestyle brands, leveraging our expertise to navigate Southeast Asian and European markets." },
  { id: 2, title: "Brand Storytelling", icon: "Feather", description: "Our storytelling services transform your brand's values into engaging narratives that resonate with audiences, creating emotional connections rooted in Japanese culture." },
  { id: 3, title: "Influencer Collaborations", icon: "Users", description: "We connect your brand with influential voices in the beauty and lifestyle sectors, enhancing brand visibility and fostering consumer trust across diverse markets." },
  { id: 4, title: "Curation of Excellence", icon: "Award", description: "We specialize in curating a select portfolio of high-quality Japanese brands that embody elegance and cultural richness, ensuring authenticity in every offering." },
];

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // Email verification hook
  const emailVerification = useEmailVerification();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotation for carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % brands.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % brands.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + brands.length) % brands.length);
  }, [currentSlide, goToSlide]);

  // Get visible slides for carousel (5 items: 2 left, center, 2 right)
  const getVisibleSlides = () => {
    const slides = [];
    for (let i = -2; i <= 2; i++) {
      const index = (currentSlide + i + brands.length) % brands.length;
      slides.push({ ...brands[index], position: i });
    }
    return slides;
  };

  return (
    <div className="min-h-screen bg-onyx">
      <Helmet>
        <title>Luxe Me Japan - Premium Japanese Beauty & Lifestyle Brands</title>
        <meta name="description" content="Connecting Japan's finest beauty, skincare, and lifestyle brands with global markets. Experience authentic Japanese luxury curated for excellence." />
      </Helmet>
      
      {/* Hero Section */}
      <header className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=2070&auto=format&fit=crop"
            className="w-full h-full object-cover scale-110 opacity-60 brightness-75"
            alt="Japan Landscape"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-onyx/10 via-transparent to-onyx"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl mb-6 sm:mb-8 opacity-0 animate-fade-up">
            Experience the Essence of <br />
            <span className="text-gradient">Japanese Elegance</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl font-light tracking-wide sm:tracking-widest text-platinum/70 mb-8 sm:mb-12 max-w-2xl mx-auto opacity-0 animate-fade-up stagger-1">
            Discover authentic Japanese beauty and lifestyle brands that embody
            tradition, elegance, and innovation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 opacity-0 animate-fade-up stagger-2">
            <Link
              to="/brands"
              className="bg-gold text-onyx px-8 sm:px-10 py-3 sm:py-4 rounded-full font-medium tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 shine-effect"
            >
              Explore Brands
            </Link>
            <Link
              to="/about"
              className="border border-gold/40 text-gold px-8 sm:px-10 py-3 sm:py-4 rounded-full font-medium tracking-widest uppercase text-xs hover:bg-gold/10 transition-all duration-500"
            >
              Our Story
            </Link>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-40">
          <ChevronDown className="w-8 h-8 text-gold" />
        </div>
      </header>

      {/* About Section with Sakura and Badge */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-ebony relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative group order-2 lg:order-1">
            <div className="absolute -inset-4 border border-gold/20 rounded-2xl group-hover:border-gold/50 transition-all duration-700 hidden sm:block"></div>
            <img
              src="/images/about-sakura.jpg"
              alt="Sakura - Japanese Heritage"
              className="relative z-10 rounded-xl grayscale hover:grayscale-0 transition-all duration-1000 object-cover aspect-[4/3] sm:aspect-[4/5] w-full shadow-2xl"
            />
            {/* Prominent Badge - "Authenticity Redefined" */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:-bottom-8 sm:-right-8 z-20">
              <div className="bg-gradient-to-r from-gold to-[#d4b068] px-6 sm:px-8 py-4 sm:py-5 rounded-2xl shadow-2xl shadow-gold/30 transform hover:scale-105 transition-transform duration-500">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-onyx animate-pulse"></div>
                  <p className="font-serif text-xl sm:text-2xl lg:text-3xl text-onyx italic leading-tight whitespace-nowrap">
                    Authenticity Redefined
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
            <div className="inline-flex items-center gap-4 text-gold tracking-[0.4em] text-xs uppercase">
              <span className="w-8 sm:w-12 h-px bg-gold"></span> Our Story
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight">
              A Bridge Between <br />
              <span className="text-gold">Two Worlds.</span>
            </h2>
            <p className="text-platinum/60 leading-relaxed text-base sm:text-lg font-light">
              Founded by seasoned professionals with decades of expertise, Luxe Me
              Japan bridges Japan's aesthetic with global markets, fostering
              authentic cultural connections.
            </p>
            <p className="text-platinum/60 leading-relaxed text-base sm:text-lg font-light">
              We specialize in seamless international brand expansions, merging
              tradition with modern insights. Our philosophy is rooted in the
              "Curation of Excellence."
            </p>
            <div className="pt-6 sm:pt-8">
              <div className="grid grid-cols-2 gap-8 sm:gap-12 border-t border-gold/10 pt-8 sm:pt-12">
                <div>
                  <p className="text-2xl sm:text-3xl font-serif text-gold">20+</p>
                  <p className="text-[10px] sm:text-xs uppercase tracking-widest text-platinum/40 mt-2">
                    Years of Expertise
                  </p>
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-serif text-gold">Global</p>
                  <p className="text-[10px] sm:text-xs uppercase tracking-widest text-platinum/40 mt-2">
                    Market Reach
                  </p>
                </div>
              </div>
            </div>
            <Link
              to="/about"
              className="inline-flex items-center gap-3 text-gold tracking-[0.2em] text-xs uppercase hover:gap-5 transition-all duration-300"
            >
              Read More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-onyx">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-24">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4 sm:mb-6">
              Our Premium <span className="italic text-gold">Services</span>
            </h2>
            <div className="w-16 sm:w-24 h-px bg-gold mx-auto"></div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-px bg-gold/10 border border-gold/10 rounded-2xl overflow-hidden">
            {services.map((service) => {
              const IconComponent = { Globe, Feather, Users, Award }[service.icon] || Globe;
              return (
                <div
                  key={service.id}
                  className="bg-ebony p-6 sm:p-10 hover:bg-gold group transition-all duration-700 cursor-default"
                >
                  <IconComponent className="w-8 sm:w-10 h-8 sm:h-10 text-gold group-hover:text-onyx mb-6 sm:mb-8 transition-colors" />
                  <h3 className="font-serif text-xl sm:text-2xl mb-3 sm:mb-4 group-hover:text-onyx transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-platinum/50 group-hover:text-onyx/80 text-sm leading-relaxed transition-colors">
                    {service.description.slice(0, 100)}...
                  </p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-3 text-gold tracking-[0.2em] text-xs uppercase hover:gap-5 transition-all duration-300"
            >
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Brands Carousel Section */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-ebony overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 sm:mb-16 gap-4 sm:gap-6">
            <div className="max-w-xl">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-3 sm:mb-4">
                Successful <span className="italic text-gold">Partnerships</span>
              </h2>
              <p className="text-platinum/50 font-light tracking-wide text-sm sm:text-base">
                A curated selection of excellence across beauty, skincare, and lifestyle.
              </p>
            </div>
            <Link
              to="/brands"
              className="text-gold tracking-[0.3em] text-[10px] uppercase font-bold flex items-center gap-4 hover:translate-x-2 transition-transform"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-0 sm:-left-4 lg:-left-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-onyx/80 backdrop-blur-sm border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-onyx transition-all duration-500 hover:scale-110 group"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 group-hover:-translate-x-0.5 transition-transform" />
            </button>
            
            <button
              onClick={nextSlide}
              className="absolute right-0 sm:-right-4 lg:-right-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-onyx/80 backdrop-blur-sm border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-onyx transition-all duration-500 hover:scale-110 group"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-0.5 transition-transform" />
            </button>

            {/* Carousel Track - UNIFORM DESIGN */}
            <div className="flex items-center justify-center gap-3 sm:gap-4 lg:gap-6 py-8 sm:py-12 px-12 sm:px-16 lg:px-20 overflow-hidden">
              {getVisibleSlides().map((brand) => {
                const isCenter = brand.position === 0;
                const isAdjacent = Math.abs(brand.position) === 1;

                return (
                  <div
                    key={`${brand.id}-${brand.position}`}
                    className={`relative flex-shrink-0 rounded-2xl overflow-hidden transition-all duration-700 ease-out cursor-pointer
                      ${isCenter 
                        ? 'w-48 sm:w-64 lg:w-80 h-64 sm:h-80 lg:h-96 z-20 scale-100 opacity-100' 
                        : isAdjacent 
                          ? 'w-32 sm:w-44 lg:w-56 h-48 sm:h-60 lg:h-72 z-10 scale-90 opacity-70 hidden sm:block' 
                          : 'w-24 sm:w-32 lg:w-40 h-36 sm:h-44 lg:h-52 z-0 scale-75 opacity-40 hidden lg:block'
                      }
                    `}
                    onClick={() => !isCenter && goToSlide(brands.findIndex(b => b.id === brand.id))}
                  >
                    {/* Background - Same structure for all */}
                    {brand.isLogo ? (
                      <div className={`absolute inset-0 bg-gradient-to-br from-gray-100 via-white to-gray-50 flex items-center justify-center p-4 sm:p-6 lg:p-8 transition-all duration-700 ${
                        isCenter ? '' : 'grayscale'
                      }`}>
                        <img
                          src={brand.image}
                          alt={brand.name}
                          className="max-w-[65%] max-h-[45%] object-contain"
                        />
                      </div>
                    ) : (
                      <img
                        src={brand.image}
                        alt={brand.name}
                        className={`w-full h-full object-cover transition-all duration-700 ${
                          isCenter ? 'grayscale-0' : 'grayscale'
                        }`}
                      />
                    )}
                    
                    {/* Gradient Overlay - SAME for all */}
                    <div className={`absolute inset-0 bg-gradient-to-t from-onyx via-onyx/30 to-transparent transition-opacity duration-500 ${isCenter ? 'opacity-90' : 'opacity-70'}`}></div>
                    
                    {/* Brand Info - SAME layout for all, visible on center */}
                    <div className={`absolute bottom-0 left-0 p-3 sm:p-5 lg:p-6 w-full transition-all duration-500 ${isCenter ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                      <span className="text-gold uppercase tracking-[0.3em] text-[8px] sm:text-[10px] block mb-1">
                        {brand.category}
                      </span>
                      <h4 className="font-serif text-base sm:text-xl lg:text-2xl text-platinum">{brand.name}</h4>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2 sm:gap-3 mt-4 sm:mt-8">
              {brands.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-1.5 sm:h-2 rounded-full transition-all duration-500 ${
                    currentSlide === index 
                      ? 'bg-gold w-6 sm:w-8' 
                      : 'bg-platinum/20 w-1.5 sm:w-2 hover:bg-gold/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events CTA */}
      <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2070&auto=format&fit=crop"
            className="w-full h-full object-cover brightness-[0.2]"
            alt="Events"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-4 text-gold tracking-[0.4em] text-xs uppercase mb-6 sm:mb-8">
            <span className="w-8 sm:w-12 h-px bg-gold"></span> Upcoming Events
            <span className="w-8 sm:w-12 h-px bg-gold"></span>
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight mb-6 sm:mb-8">
            Join Us on a Journey of <br />
            <span className="italic text-gradient">Exploration</span>
          </h2>
          <p className="text-platinum/70 text-base sm:text-lg font-light mb-8 sm:mb-12 max-w-2xl mx-auto">
            Experience exclusive events, workshops, and brand launches that celebrate
            the art of Japanese beauty and lifestyle.
          </p>
          <Link
            to="/events"
            className="bg-gold text-onyx px-8 sm:px-12 py-4 sm:py-5 rounded-full font-medium tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 inline-block shine-effect"
          >
            Explore Events
          </Link>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-onyx">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-4 sm:mb-6">
              Why Choose <span className="text-gold italic">Luxe Me Japan</span>
            </h2>
            <div className="w-16 sm:w-24 h-px bg-gold mx-auto"></div>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
            <div className="text-center group">
              <div className="w-16 sm:w-20 h-16 sm:h-20 mx-auto mb-6 sm:mb-8 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold/10 transition-all duration-500">
                <Globe className="w-6 sm:w-8 h-6 sm:h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl mb-3 sm:mb-4">Market Expertise</h3>
              <p className="text-platinum/50 leading-relaxed text-sm sm:text-base">
                In-depth knowledge of Southeast Asian and European markets tailored
                to your brand's vision.
              </p>
            </div>
            <div className="text-center group">
              <div className="w-16 sm:w-20 h-16 sm:h-20 mx-auto mb-6 sm:mb-8 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold/10 transition-all duration-500">
                <Users className="w-6 sm:w-8 h-6 sm:h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl mb-3 sm:mb-4">Strategic Partnership</h3>
              <p className="text-platinum/50 leading-relaxed text-sm sm:text-base">
                Strong connections with local influencers and retailers enhancing
                brand visibility.
              </p>
            </div>
            <div className="text-center group sm:col-span-2 md:col-span-1">
              <div className="w-16 sm:w-20 h-16 sm:h-20 mx-auto mb-6 sm:mb-8 rounded-full border border-gold/30 flex items-center justify-center group-hover:bg-gold/10 transition-all duration-500">
                <Feather className="w-6 sm:w-8 h-6 sm:h-8 text-gold" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl mb-3 sm:mb-4">Cultural Storytelling</h3>
              <p className="text-platinum/50 leading-relaxed text-sm sm:text-base">
                We convey the rich narrative behind each brand, creating emotional
                connections with consumers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-ebony">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-6 sm:mb-8">
            Embark on a Journey <br />
            <span className="text-gold italic">with Us Today</span>
          </h2>
          <p className="text-platinum/60 text-base sm:text-lg font-light mb-8 sm:mb-12">
            Let's shape the future of Japanese beauty and lifestyle brands together.
          </p>
          <button
            onClick={emailVerification.handleEmailClick}
            className="bg-gold text-onyx px-8 sm:px-12 py-4 sm:py-5 rounded-full font-medium tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 inline-block shine-effect"
          >
            Get in Touch
          </button>
        </div>
      </section>

      {/* Email Verification Modal */}
      <EmailVerificationModal {...emailVerification} />
    </div>
  );
};

export default HomePage;
